import React, { useEffect, useRef, useState } from 'react';
import { PropTypes } from 'prop-types';
import ValidationInspector from 'common/Validation/ValidationInspector';

const WhdValidationSummary = ({ validationResult, sectionTitle, className, ignoreCodes, specificCodes }) => {
  const warnings = ValidationInspector.filterWarnings(validationResult, specificCodes, ignoreCodes);
  const rejections = ValidationInspector.filterRejections(validationResult, specificCodes, ignoreCodes);

  const hasWarnings = warnings && warnings.length > 0;
  const hasRejections = rejections && rejections.length > 0;

  if (!validationResult || !(rejections || warnings)) {
    return null;
  }

  const quantRejections = rejections.length === 1 ? 'a rejection' : `${ rejections.length } rejections`;
  const quantWarnings = warnings.length === 1 ? 'a warning' : `${ warnings.length } warnings`;

  const [isDetailedVisible, setIsDetailedVisible] = useState(false);

  const myRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (myRef.current && !myRef.current.contains(event.target)) {
        setIsDetailedVisible(false);
      }
    }

    function handleEscPress(event) {
      if (myRef.current && !myRef.current.contains(event.target)) {
        setIsDetailedVisible(false);
      }

      if (event.keyCode === 27) {
        setIsDetailedVisible(false);
      }
    }

    document.addEventListener('keyup', handleEscPress);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('keyup', handleEscPress);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [myRef]);

  return <div className={ `validation-container ${ className }` }>
    <div className="validation-summary">
      { hasRejections && <div className="validation-error">
        <button ref={ myRef } type="button" className="text-link" onClick={ () => setIsDetailedVisible(true) }><span className="icon icon-cross icon-cross-circle mr-2" />{ sectionTitle } has { quantRejections }.</button>
      </div> }
      { hasWarnings && <div className="validation-warning">
        <button ref={ myRef } type="button" className="text-link" onClick={ () => setIsDetailedVisible(true) }><span className="icon icon-flag solid mr-2" />{ sectionTitle } has { quantWarnings }.</button>
      </div> }
    </div>
    { isDetailedVisible && <div ref={ myRef } className="columns validation-detailed" tabIndex={ -1 }>
      <button type="button" className="btn btn-clear float-right" onClick={ () => setIsDetailedVisible(false) } />
      { hasRejections && <div className="col-12 py-1">
        <h3>Rejections</h3>
        <ul>{ rejections.map(x => <ValidationResultItem validationEntry={ x } key={ x.key } className="col-12" />) }</ul>
      </div> }
      { hasWarnings && <div className="col-12 py-1">
        <h3>Warnings</h3>
        <ul>{ warnings.map(x => <ValidationResultItem validationEntry={ x } key={ x.key } className="col-12" />) }</ul>
      </div> }
    </div> }
  </div>;
};

WhdValidationSummary.propTypes = {
  sectionTitle: PropTypes.string.isRequired,
  className: PropTypes.string,
  ignoreCodes: PropTypes.arrayOf(PropTypes.string),
  specificCodes: PropTypes.arrayOf(PropTypes.string),
  validationResult: PropTypes.shape({
    valid: PropTypes.bool,
    rejections: PropTypes.arrayOf(PropTypes.shape({
      code: PropTypes.string,
      codeDescription: PropTypes.string,
      key: PropTypes.string
    })),
    warnings: PropTypes.arrayOf(PropTypes.shape({
      code: PropTypes.string,
      codeDescription: PropTypes.string,
      key: PropTypes.string
    }))
  })
};

WhdValidationSummary.defaultProps = {
  className: 'pt-1rem',
  ignoreCodes: null,
  specificCodes: null,
  validationResult: null
};

const ValidationResultItem = ({ validationEntry, className }) => {
  const resultType = validationEntry.code.substring(0, 3);
  const isWrn = resultType === 'WRN';
  const isRej = resultType === 'REJ';
  const isRec = resultType === 'REC';
  const itemClass = (isWrn && 'validation-warning') || (isRej && 'validation-error') || (isRec && 'validation-recommendation');
  const iconClass = (isWrn && 'icon-flag solid') || (isRej && 'icon-cross icon-cross-circle') || (isRec && 'icon-message');
  return <li className={ `${ itemClass } ${ className }` }><span className={ `icon ${ iconClass }` } /><em>{ validationEntry.codeDescription }</em> (<b>{ validationEntry.code }</b>)</li>;
};

ValidationResultItem.propTypes = {
  className: PropTypes.string,
  validationEntry: PropTypes.shape({
    code: PropTypes.string,
    codeDescription: PropTypes.string,
    key: PropTypes.string
  }).isRequired
};

ValidationResultItem.defaultProps = {
  className: null
};

export default WhdValidationSummary;
